import React, { useState } from "react";
import { Box, Card, Select, ListItemIcon, ListItemText, MenuItem } from "@material-ui/core";
import FormatPaintIcon from "@material-ui/icons/FormatPaint";
import WarningIcon from "@material-ui/icons/Warning";
import { PermissionIdEnum } from "../util/types/permissions/permissionIdEnum";

import { getUserPermissionsList } from "../util/permissionsUtils";
import i18n from "i18next";

const valueToIconMap = (variantProp) => {
  const greyDeactivated = "#aaa";
  const redActivated = "#c85a5a"; // Work well if it matches the color for Zone 3
  const redPaintBrushIcon = (
    <ListItemIcon style={{ color: redActivated }}>
      <FormatPaintIcon />
    </ListItemIcon>
  );
  const blueActivated = "#073255";
  const bluePaintBrushIcon = (
    <ListItemIcon style={{ color: blueActivated }}>
      <FormatPaintIcon />
    </ListItemIcon>
  );

  let table = {
    anyhazard: {
      icon: (
        <ListItemIcon style={{ color: greyDeactivated }}>
          <WarningIcon />
        </ListItemIcon>
      ),
      text: i18n.t("climateAnalyticsSelects.noHazards"),
    },

    snow: {
      icon: redPaintBrushIcon,
      text: i18n.t("climateAnalyticsSelects.snow"),
    },
    hail: {
      icon: redPaintBrushIcon,
      text: i18n.t("climateAnalyticsSelects.hail") + " 20mm",
    },

    wind: {
      icon: redPaintBrushIcon,
      text: i18n.t("riskAssessment.highWind"),
    },
    rain: {
      icon: redPaintBrushIcon,
      text: i18n.t("riskAssessment.flashflood") + " 24h",
    },
  };

  const ligtningHazard = {
    lightning: {
      icon: redPaintBrushIcon,
      text: i18n.t("climateAnalyticsSelects.lightning"),
    },
  };

  const buildingHazards = {
    earthquake: {
      icon: redPaintBrushIcon,
      text: i18n.t("climateAnalyticsSelects.earthquake"),
    },
    ...ligtningHazard,
    weatherindex: {
      icon: (
        <ListItemIcon style={{ color: "#c85a5a" }}>
          <FormatPaintIcon />
        </ListItemIcon>
      ),
      text: i18n.t("climateAnalyticsSelects.weatherIndex"),
    },
  };

  if (variantProp !== "AgroClimateAnalytics") {
    // Note, it can be null
    table = { ...table, ...buildingHazards };
  } else {
    table = { ...table, ...ligtningHazard };
  }

  let aniaTable = {
    aniaWindstorm: {
      icon: bluePaintBrushIcon,
      text: "ANIA " + i18n.t("riskAssessment.windHazard"),
      permission: PermissionIdEnum.WindItaANIA,
    },

    aniaPrecipitation: {
      icon: bluePaintBrushIcon,
      text: "ANIA " + i18n.t("riskAssessment.precipitation") + " 75 mm",
      permission: PermissionIdEnum.PrecipitationIta75mmANIA,
    },
    aniaHail: {
      icon: bluePaintBrushIcon,
      text: "ANIA " + i18n.t("riskAssessment.hail"),
      permission: PermissionIdEnum.HailItaANIA,
    },
    aniaSnow: {
      icon: bluePaintBrushIcon,
      text: "ANIA " + i18n.t("riskAssessment.precipitationSnow") + " 30 mm",
      permission: PermissionIdEnum.SnowIta30cmANIA,
    },
    aniaLightning: {
      icon: bluePaintBrushIcon,
      text: "ANIA " + i18n.t("riskAssessment.lightningStrike"),
      permission: PermissionIdEnum.LightningItaANIA,
    },
  };

  const cysIndexesTable = {
    cysNcat: {
      icon: redPaintBrushIcon,
      text: i18n.t("climateIndex.CysNcat"),
      permission: PermissionIdEnum.CysItaNonCatIndex,
    },
    cysNat: {
      icon: redPaintBrushIcon,
      text: i18n.t("climateIndex.CysNe"),
      permission: PermissionIdEnum.CysItaNatEventsIndex,
    },
    // cysCatastr: { icon: redPaintBrushIcon, text: t("climateIndex.CysCatastr") }, // To be handled in RR-709
  };

  const aniaIndexesTable = {
    aniaNcat: {
      icon: bluePaintBrushIcon,
      text: i18n.t("climateIndex.AniaNcat"),
      permission: PermissionIdEnum.AniaNonCatIndex,
    },
    aniaNat: {
      icon: bluePaintBrushIcon,
      text: i18n.t("climateIndex.AniaNe"),
      permission: PermissionIdEnum.AniaNatEventsIndex,
    },
    // aniaCatastr: { icon: redPaintBrushIcon, text: i18n.t("climateIndex.AniaCatastr") }, // To be handled in RR-709
  };

  const realeGroupWindAndHailTable = {
    realeGroupWind: {
      icon: redPaintBrushIcon,
      text: i18n.t("riskAssessment.realeGroupWind"),
      permission: PermissionIdEnum.RealeGroupWind,
    },
    realeGroupHail: {
      icon: redPaintBrushIcon,
      text: i18n.t("riskAssessment.realeGroupHail"),
      permission: PermissionIdEnum.RealeGroupHail,
    },
  };

  const howdenInlandFlood202410Table = {
    howdenWeightedInlandFlood202410: {
      icon: redPaintBrushIcon,
      text: i18n.t("riskAssessment.howdenInlandFlood202410"),
      extraHazardText: i18n.t("riskExposureMapExplained.inlandFloodHowdenExpiration"),
      permission: PermissionIdEnum.InlandFloodHowden202410,
    },
  };

  const allUserPermissions = getUserPermissionsList();
  const customTables = [
    cysIndexesTable,
    aniaTable,
    aniaIndexesTable,
    realeGroupWindAndHailTable,
    howdenInlandFlood202410Table,
  ];

  // Build one merged object with only entries the user has permission for
  const filteredCustomTables = customTables.reduce((acc, tableObj) => {
    Object.entries(tableObj).forEach(([key, item]) => {
      if (allUserPermissions.includes(item.permission)) {
        acc[key] = item;
      }
    });
    return acc;
  }, {});

  // Merge into main table
  table = { ...table, ...filteredCustomTables };

  // if (permissions.includes("super") || permissions.includes("CYS_ANALYTICS_CYSINDEX")) {
  //   table = { ...table, ...cysIndexesTable };
  // }

  // if (permissions.includes("super") || permissions.includes("CYS_ANALYTICS_ANIA")) {
  //   table = { ...table, ...aniaTable };
  // }

  // if (permissions.includes("super") || permissions.includes("CYS_ANALYTICS_ANIAINDEX")) {
  //   table = { ...table, ...aniaIndexesTable };
  // }
  // if (permissions.includes("super") || permissions.includes("CYS_ANALYTICS_REALEGROUP_WIND_AND_HAIL")) {
  //   table = { ...table, ...realeGroupWindAndHailTable };
  // }
  // if (permissions.includes("super") || permissions.includes("CYS_ANALYTICS_HOWDEN_INLAND_FLOOD")) {
  //   table = { ...table, ...howdenInlandFlood202410Table };
  // }

  return table;
};

export const getValueToIconMapForHazard = (selectedValue, variantProp) => {
  return valueToIconMap(variantProp)[selectedValue];
};

export const HazardSelectorTool = (hazardSelect, setHazardSelect, variantProp) => {
  const menuStyle = {
    position: "absolute",
    top: 250,
    left: 0,
    padding: "10px",
  };
  const [openSelect, setOpenSelect] = useState(false);

  const renderGivenValue = (selectedValue) => {
    const iconText = getValueToIconMapForHazard(selectedValue, variantProp);

    return (
      <Box
        sx={{
          flexDirection: "row",
          display: "flex",
        }}
      >
        {iconText.icon}
        <ListItemText primary={iconText.text} />
      </Box>
    );
  };

  const createMenuItem = (key, value) => {
    return (
      <MenuItem key={key} value={key}>
        {value.icon}
        <ListItemText primary={value.text}></ListItemText>
      </MenuItem>
    );
  };

  const createAllMenuItems = () => {
    const allItems = Object.entries(valueToIconMap(variantProp)).map(([key, value]) =>
      createMenuItem(key, value),
    );
    return allItems;
  };
  return (
    <Box style={menuStyle}>
      <Card>
        <Select
          value={hazardSelect}
          open={openSelect}
          onClose={() => setOpenSelect(false)}
          onOpen={() => setOpenSelect(true)}
          onChange={(e) => {
            setHazardSelect(e.target.value);
          }}
          renderValue={(value) => renderGivenValue(value)}
        >
          {createAllMenuItems()}
        </Select>
      </Card>
    </Box>
  );
};
